import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import adminTheme from "./theme/adminTheme";
import { trackPageView } from "./utils/analytics";

import "./App.css";

import Home from "../src/Pages/Home";
import About from "../src/Pages/About";
import Shop from "../src/Pages/Shop";
import Contact from "../src/Pages/Contact";
import Blog from "../src/Pages/Blog";
import Header from "../src/Components/Header/Navbar";
import AnnouncementBar from "./Components/AnnouncementBar/AnnouncementBar";
import Footer from "../src/Components/Footer/Footer";
import ProductDetails from "./Pages/ProductDetails";
import NotFound from "./Pages/NotFound";
import ScrollToTop from "./Components/ScrollButton/ScrollToTop";
import Authentication from "./Pages/Authentication";
import ResetPass from "./Components/Authentication/Reset/ResetPass";
import BlogDetails from "./Components/Blog/BlogDetails/BlogDetails";
import TermsConditions from "./Pages/TermsConditions";
import PrivacyPolicy from "./Pages/legal/PrivacyPolicy";
import CookiePolicy from "./Pages/legal/CookiePolicy";
import ShippingPolicy from "./Pages/legal/ShippingPolicy";
import RefundPolicy from "./Pages/legal/RefundPolicy";
import ShoppingCart from "./Components/ShoppingCart/ShoppingCart";
import Payment from "./Pages/Payment/Payment";
import PaymentSuccess from "./Pages/Payment/PaymentSuccess";
import PageBySlug from "./Pages/PageBySlug";
import Career from "./Pages/Career";
import FestiveVibes from "./Pages/FestiveVibes";
import Occasions from "./Pages/Occasions";
import Popup from "./Components/PopupBanner/Popup";
import CookieConsent from "./Components/CookieConsent/CookieConsent";
import LocationOnboarding from "./Components/LocationOnboarding/LocationOnboarding";
import CartLeaveReminder from "./Components/CartLeaveReminder/CartLeaveReminder";
import BrowseCloseFollowUp from "./Components/BrowseCloseFollowUp/BrowseCloseFollowUp";
import ChatWidget from "./Components/ChatWidget/ChatWidget";
import { Toaster } from "react-hot-toast";
import AdminLogin from "./Pages/Admin/AdminLogin";
import AdminForgotPassword from "./Pages/Admin/AdminForgotPassword";
import AdminResetPassword from "./Pages/Admin/AdminResetPassword";
import AdminLayout from "./Pages/Admin/AdminLayout";
import AdminCategories from "./Pages/Admin/AdminCategories";
import AdminProducts from "./Pages/Admin/AdminProducts";
import AdminNeedAttention from "./Pages/Admin/AdminNeedAttention";
import AdminOrders from "./Pages/Admin/AdminOrders";
import AdminCoupons from "./Pages/Admin/AdminCoupons";
import AdminCheckoutSettings from "./Pages/Admin/AdminCheckoutSettings";
import AdminAvailability from "./Pages/Admin/AdminAvailability";
import AdminSync from "./Pages/Admin/AdminSync";
import AdminCsvSync from "./Pages/Admin/AdminCsvSync";
import AdminFirebase from "./Pages/Admin/AdminFirebase";
import AdminPages from "./Pages/Admin/AdminPages";
import AdminBlogPosts from "./Pages/Admin/AdminBlogPosts";
import AdminHomeGallery from "./Pages/Admin/AdminHomeGallery";
import AdminHeroSliders from "./Pages/Admin/AdminHeroSliders";
import AdminFestivalSchedule from "./Pages/Admin/AdminFestivalSchedule";
import AdminPageEditor from "./Pages/Admin/AdminPageEditor";
import AdminMenu from "./Pages/Admin/AdminMenu";
import AdminDashboard from "./Pages/Admin/AdminDashboard";
import AdminProductAdd from "./Pages/Admin/AdminProductAdd";
import AdminVariations from "./Pages/Admin/AdminVariations";
import AdminAccount from "./Pages/Admin/AdminAccount";
import AdminStaff from "./Pages/Admin/AdminStaff";
import AdminRoles from "./Pages/Admin/AdminRoles";
import AdminActivity from "./Pages/Admin/AdminActivity";
import AdminCustomers from "./Pages/Admin/AdminCustomers";
import AdminRentalOutward from "./Pages/Admin/AdminRentalOutward";
import AdminRentalInward from "./Pages/Admin/AdminRentalInward";
import AdminRentalLaundry from "./Pages/Admin/AdminRentalLaundry";
import AdminRentalDamaged from "./Pages/Admin/AdminRentalDamaged";
import AdminRentalKanban from "./Pages/Admin/AdminRentalKanban";
import AdminNotifications from "./Pages/Admin/AdminNotifications";
import AdminErrorBoundary from "./Pages/Admin/AdminErrorBoundary";
import AccountLayout from "./Pages/Account/AccountLayout";
import AccountDashboard from "./Pages/Account/AccountDashboard";
import AccountProfile from "./Pages/Account/AccountProfile";
import AccountOrders from "./Pages/Account/AccountOrders";
import AccountPaymentMethods from "./Pages/Account/AccountPaymentMethods";
import { getToken } from "./api/client";
import { SiteSeoProvider } from "./context/SiteSeoContext";
import GlobalJsonLd, { GlobalVerificationHelmet } from "./Components/Seo/GlobalJsonLd";
import AdminSeoSettings from "./Pages/Admin/AdminSeoSettings";
import AdminAnnouncementBar from "./Pages/Admin/AdminAnnouncementBar";
import AdminSeoChecker from "./Pages/Admin/AdminSeoChecker";
import CustomerCartSync from "./Components/CustomerCartSync/CustomerCartSync";
import WishlistPage from "./Pages/WishlistPage";
import CategoryLanding from "./Pages/CategoryLanding";
import FloatingWhatsAppCta from "./Components/FloatingWhatsAppCta/FloatingWhatsAppCta";

function AdminLayoutOrRedirect() {
  const token = getToken();
  if (!token) return <Navigate to="/admin/login" replace />;
  return <AdminLayout />;
}

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (!isAdmin) trackPageView(`${location.pathname}${location.search}`);
  }, [isAdmin, location.pathname, location.search]);

  if (isAdmin) {
    return (
      <ThemeProvider theme={adminTheme}>
        <CssBaseline />
        <AdminErrorBoundary>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route path="/admin" element={<AdminLayoutOrRedirect />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="account" element={<AdminAccount />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/needs-attention" element={<AdminNeedAttention />} />
            <Route path="products/add" element={<AdminProductAdd />} />
            <Route path="products/edit/:id" element={<AdminProductAdd />} />
            <Route path="variations" element={<AdminVariations />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="settings/checkout" element={<AdminCheckoutSettings />} />
            <Route path="settings/seo" element={<AdminSeoSettings />} />
            <Route path="settings/seo-checker" element={<AdminSeoChecker />} />
            <Route path="settings/announcement" element={<AdminAnnouncementBar />} />
            <Route path="availability" element={<AdminAvailability />} />
            <Route path="sync" element={<AdminSync />} />
            <Route path="sync/csv" element={<AdminCsvSync />} />
            <Route path="firebase" element={<AdminFirebase />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="blog" element={<AdminBlogPosts />} />
            <Route path="home-gallery" element={<AdminHomeGallery />} />
            <Route path="pages/hero-sliders" element={<AdminHeroSliders />} />
            <Route path="pages/festival-schedule" element={<AdminFestivalSchedule />} />
            <Route path="pages/edit/:slug" element={<AdminPageEditor />} />
            <Route path="menu" element={<AdminMenu />} />
            <Route path="staff" element={<AdminStaff />} />
            <Route path="roles" element={<AdminRoles />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="rental" element={<Navigate to="kanban" replace />} />
            <Route path="rental/outward" element={<AdminRentalOutward />} />
            <Route path="rental/inward" element={<AdminRentalInward />} />
            <Route path="rental/laundry" element={<AdminRentalLaundry />} />
            <Route path="rental/damaged" element={<AdminRentalDamaged />} />
            <Route path="rental/kanban" element={<AdminRentalKanban />} />
            <Route path="notifications" element={<AdminNotifications />} />
          </Route>
        </Routes>
        </AdminErrorBoundary>
      </ThemeProvider>
    );
  }
  return (
    <SiteSeoProvider>
      <CustomerCartSync />
      <GlobalVerificationHelmet />
      <GlobalJsonLd />
      <LocationOnboarding />
      <CartLeaveReminder />
      <BrowseCloseFollowUp />
      <FloatingWhatsAppCta />
      <AnnouncementBar />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/category/:slug" element={<CategoryLanding />} />
        <Route path="/festive-vibes" element={<FestiveVibes />} />
        <Route path="/occasions" element={<Occasions />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/career" element={<Career />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogDetails />} />
        <Route path="/product" element={<Navigate to="/shop" replace />} />
        <Route path="/product/:id" element={<ProductDetails />} />
        <Route path="/loginSignUp" element={<Authentication />} />
        <Route path="/resetPassword" element={<ResetPass />} />
        <Route path="/account" element={<AccountLayout />}>
          <Route index element={<AccountDashboard />} />
          <Route path="profile" element={<AccountProfile />} />
          <Route path="orders" element={<AccountOrders />} />
          <Route path="payment-methods" element={<AccountPaymentMethods />} />
        </Route>
        <Route path="/BlogDetails" element={<Navigate to="/blog" replace />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/cart" element={<ShoppingCart />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/:slug" element={<PageBySlug />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
      <ChatWidget />
    </SiteSeoProvider>
  );
}

const App = () => {
  return (
    <>
      <Popup />
      <ScrollToTop />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <CookieConsent />
        <AppContent />
        <Toaster />
      </BrowserRouter>
    </>
  );
};

export default App;
